import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useNavigate } from "react-router";
import CustomButton from "./../Components/Utils/Custom_Btn";
import SpecialReservationDialog from "../Components/SpecialReservationDialog";
import { adjustDateForDisplay, formatTimeForDisplay } from "../Utils/timeUtils";
import {
  Button,
  Box,
  Typography, 
  Dialog, 
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  CircularProgress,
  styled,
  Popover,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import EditIcon from '@mui/icons-material/Edit';
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { fetchdateOfReservationAutocomplete } from "../Redux/Reservations_Slice";
import { CreateSpecialReservation, FetchEditReservations, GetAllReservationsApi } from "../Apis/Reservations/Reservations";
import { useToast } from "../Hooks/useToast";
import deLocale from "@fullcalendar/core/locales/de";

const primaryColor = "#D6B981"; // اللون الذهبي الأساسي للموقع
const secondaryColor = "#f5f5f5";
// حد أقصى كبير بما يكفي لجلب كل الحجوزات دفعة واحدة بدل حد ثابت 1000
// كان يقطع الحجوزات الأقدم (مرتّبة سابقاً حسب تاريخ الإنشاء) عندما يتجاوز العدد الكلي 1000
const ALL_RESERVATIONS_FETCH_LIMIT = 100000;

const StyleWrapper = styled(Box)(({ theme }) => ({
  "& .fc": {
    fontFamily: theme.typography.fontFamily,
    "& .fc-toolbar-title": {
      fontSize: "1.75rem",
      fontWeight: 700,
      color: primaryColor,
      letterSpacing: "-0.5px",
    },
    "& .fc-button": {
      backgroundColor: primaryColor,
      borderColor: primaryColor,
      color: "#fff",
      fontWeight: 600,
      textTransform: "capitalize",
      padding: "8px 16px",
      borderRadius: "8px",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#a57122",
        borderColor: "#a57122",
        boxShadow: "0 4px 8px rgba(190, 135, 42, 0.3)",
      },
      "&:focus": {
        boxShadow: "0 0 0 3px rgba(190, 135, 42, 0.3)",
      },
    },
    "& .fc-daygrid-day": {
      transition: "all 0.3s",
      "&:hover": {
        backgroundColor: secondaryColor,
      },
    },
    "& .fc-daygrid-day.fc-day-today": {
      backgroundColor: `${primaryColor}15`,
    },
    "& .fc-daygrid-day-number": {
      fontSize: "1rem",
      fontWeight: 500,
      padding: "8px",
    },
    "& .fc-event": {
      borderRadius: "6px",
      border: "none",
      backgroundColor: primaryColor,
      color: "#fff",
      width: "100%",
      fontSize: "clamp(0.75rem, 1vw, 0.9rem)",
      padding: "4px 8px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      margin: "1px 0",
      "&:hover": {
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
        transform: "translateY(-2px)",
      },
    },
    "& .fc-daygrid-more-link": {
      backgroundColor: "#fff",
      color: primaryColor,
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: "12px",
      margin: "2px 0",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      border: `1px solid ${primaryColor}30`,
      transition: "all 0.2s ease",
      position: "relative",
      zIndex: 5,
      "&:hover": {
        backgroundColor: `${primaryColor}10`,
        boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
        transform: "translateY(-1px)",
        textDecoration: "none",
      },
    },
    "& .fc-daygrid-day-bottom": {
      padding: "2px 0",
    },
    "& .fc-day-other .fc-daygrid-day-number": {
      opacity: 0.5,
    },
    "& .fc-col-header-cell": {
      padding: "8px 0",
      fontWeight: 600,
      color: "#555",
    },
    "& .fc-timegrid-slot-label": {
      fontSize: "0.85rem",
      color: "#666",
    },
    "& .fc-timegrid-axis": {
      fontSize: "0.85rem",
      color: "#666",
    },
    "& .fc-list-day-cushion": {
      backgroundColor: `${primaryColor}20`,
    },
    "& .fc-list-event:hover td": {
      backgroundColor: `${primaryColor}10`,
    },
  },
}));

const KalenderReservierung = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const data = useSelector((state) => state.Reservations);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedReservations, setSelectedReservations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allReservations, setAllReservations] = useState([]);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoveredEventEl, setHoveredEventEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredReservations, setFilteredReservations] = useState([]);
  
  // Special reservation dialog states
  const [specialReservationDialogOpen, setSpecialReservationDialogOpen] = useState(false);
  const [specialReservationLoading, setSpecialReservationLoading] = useState(false);
  const { toastSuccess, toastError } = useToast();

  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerStreet: "",
    customerPLZ: "",
    customerCity: "",
    status: "",
    adminNote: "",
    note: "",
    dateOfReservation: null,
    dateEndOfReservation: null
  });
  const [isUpdatingTime, setIsUpdatingTime] = useState(false);
  const [isRefreshingSelection, setIsRefreshingSelection] = useState(false);
  const [isResolvingReservationId, setIsResolvingReservationId] = useState(false);

  // دالة لتحديد لون النقطة بناءً على طريقة الدفع
  const getPaymentDotColor = (reservation) => {
    if (reservation.isSpecial || reservation.payWay === 'special') {
      return '#FFD700'; // أصفر للحجوزات المميزة
    } else if (reservation.payWay === 'paypal') {
      return '#4caf50'; // أخضر للبايبال
    } else {
      return '#f44336'; // أحمر للكود
    }
  };

  // دالة لإضافة حجز مميز
  const handleAddSpecialReservation = () => {
    console.log("🌟 Opening special reservation dialog");
    setSpecialReservationDialogOpen(true);
  };

  // دالة لإغلاق نافذة الحجز المميز
  const handleCloseSpecialReservation = () => {
    setSpecialReservationDialogOpen(false);
  };

  // دالة لإنشاء الحجز المميز - نفس تطبيق الصفحة الرئيسية (JSON إلى /special)
  const handleCreateSpecialReservation = async (submissionData) => {
    console.log("🔍 Calendar received data:", submissionData);
    setSpecialReservationLoading(true);
    
    // استخدام البيانات المُرسلة من ��لنموذج
    const dataToUse = submissionData;
    
    // التحقق من صحة البيانات
    if (!dataToUse.title || !dataToUse.title.trim()) {
      toastError('Bitte einen Titel für die Reservierung eingeben.');
      setSpecialReservationLoading(false);
      return;
    }

    if (!dataToUse.date) {
      toastError('Bitte ein Datum auswählen.');
      setSpecialReservationLoading(false);
      return;
    }

    if (!dataToUse.startTime || !dataToUse.endTime) {
      toastError('Bitte Start- und Endzeit auswählen.');
      setSpecialReservationLoading(false);
      return;
    }

    // التحقق من أن وقت النهاية بعد وقت البداية
    if (dataToUse.startTime >= dataToUse.endTime) {
      toastError('Die Endzeit muss nach der Startzeit liegen.');
      setSpecialReservationLoading(false);
      return;
    }

    try {
      console.log("🌟 Creating special reservation (JSON /special)...");

      // حساب وقت البداية والنهاية محليًا ثم تحويله ISO ثابتًا (مثل الصفحة الرئيسية)
      const start = new Date(
        dataToUse.date.getFullYear(),
        dataToUse.date.getMonth(),
        dataToUse.date.getDate(),
        dataToUse.startTime.getHours(),
        dataToUse.startTime.getMinutes()
      );
      const end = new Date(
        dataToUse.date.getFullYear(),
        dataToUse.date.getMonth(),
        dataToUse.date.getDate(),
        dataToUse.endTime.getHours(),
        dataToUse.endTime.getMinutes()
      );

      const isoStringWithoutTimezone = new Date(start.getTime() - (start.getTimezoneOffset() * 60000)).toISOString();
      const durationMinutes = Math.max(30, Math.round((end.getTime() - start.getTime()) / 60000));

      const reservationData = {
        customerName: dataToUse.title.trim(),
        customerEmail: 'special@reservation.com',
        customerPhone: '000000000',
        customerStreet: 'Sonderreservierung',
        customerPLZ: '00000',
        customerCity: 'Special',
        packageName: 'Sonderreservierung',
        numberOfPersons: 1,
        dateOfReservation: isoStringWithoutTimezone,
        note: dataToUse.description || 'Sonderreservierung',
        isAGift: false,
        selectedGender: '',
        extraObjectsList: [],
        payWay: 'special',
        isSpecial: true,
        isPaid: true,
        duration: durationMinutes
      };

      const response = await CreateSpecialReservation(reservationData);
      console.log("📥 Special reservation response:", response);

      if (response.status === 201) {
        console.log("✅ Special reservation created successfully:", response.data);
        
        toastSuccess("Sonderreservierung erfolgreich erstellt!");
        
        handleCloseSpecialReservation();
        
        // إعادة تحميل البيانات
        const refreshResult = await dispatch(
          fetchdateOfReservationAutocomplete({
            page: 1,
            limit: ALL_RESERVATIONS_FETCH_LIMIT,
            dateOfReservation: "",
            isPaid: false
          })
        ).unwrap();
        
        let refreshedData = [];
        if (refreshResult && refreshResult.data) {
          if (Array.isArray(refreshResult.data)) {
            refreshedData = refreshResult.data;
          } else if (refreshResult.data.data && Array.isArray(refreshResult.data.data)) {
            refreshedData = refreshResult.data.data;
          } else if (refreshResult.data.response && Array.isArray(refreshResult.data.response.data)) {
            refreshedData = refreshResult.data.response.data;
          }
        }
        setAllReservations(refreshedData);
        
      } else {
        console.error("❌ Failed to create special reservation:", response);
        toastError(`Fehler beim Erstellen der Sonderreservierung: ${response?.data?.message || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error("❌ Error creating special reservation:", error);
      console.error("❌ Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      toastError(`Fehler beim Erstellen der Sonderreservierung: ${error.message || 'Netzwerkfehler'}`);
    } finally {
      setSpecialReservationLoading(false);
    }
  };

  // دالة مساعدة للتحقق من صحة البيانات
  const validateReservationData = (data) => {
    if (!data || typeof data !== 'object') return false;
    if (!data.dateOfReservation) return false;
    
    const date = new Date(data.dateOfReservation);
    return !isNaN(date.getTime());
  };

  // دالة مساعدة لتصفية الحجوزات بتاريخ معين
  const filterReservationsByDate = (reservations, targetDate) => {
    if (!Array.isArray(reservations) || !targetDate) return [];
    
    return reservations.filter((r) => {
      try {
        if (!validateReservationData(r)) return false;
        
        // فلترة الحجوزات لإظهار المقبولة فقط وإخفاء المرفوضة والمنتظرة
        if (r.status !== "accepted") return false;
        
        const resDate = new Date(r.dateOfReservation);
        return (
          resDate.getFullYear() === targetDate.getFullYear() &&
          resDate.getMonth() === targetDate.getMonth() &&
          resDate.getDate() === targetDate.getDate()
        );
      } catch (error) {
        console.error("Error filtering reservation:", error, r);
        return false;
      }
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dispatch(
          fetchdateOfReservationAutocomplete({
            page: 1,
            limit: ALL_RESERVATIONS_FETCH_LIMIT,
            dateOfReservation: "",
            isPaid: false
          })
        ).unwrap();

        let reservationsData = [];

        if (result && result.data) {
          if (Array.isArray(result.data)) {
            reservationsData = result.data;
          } else if (result.data.data && Array.isArray(result.data.data)) {
            reservationsData = result.data.data;
          } else if (result.data.response && Array.isArray(result.data.response.data)) {
            reservationsData = result.data.response.data;
          }
        }

        console.log("🔄 Loaded reservations:", reservationsData.length, "of total:", result?.total);
        if (result?.total && reservationsData.length < result.total) {
          console.warn("⚠️ لم يتم جلب كل الحجوزات! تم جلب", reservationsData.length, "من أصل", result.total);
        }
        setAllReservations(reservationsData);
      } catch (error) {
        console.error("❌ Fehler beim Laden der Reservierungen:", error);
        setAllReservations([]);
      }
    };
    fetchData();
  }, [dispatch]);

  // تصفية الحجوزات بناءً على البحث
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredReservations(allReservations);
    } else {
      try {
        const filtered = allReservations.filter((reservation) => {
          if (!reservation) return false;
          
          const searchLower = searchTerm.toLowerCase();
          const userName = reservation.userName ? reservation.userName.toString().toLowerCase() : '';
          const customerName = reservation.customerName ? reservation.customerName.toString().toLowerCase() : '';
          const customerEmail = reservation.customerEmail ? reservation.customerEmail.toString().toLowerCase() : '';
          const customerPhone = reservation.customerPhone ? reservation.customerPhone.toString().toLowerCase() : '';
          const reservationNumber = reservation.reservationNumber ? reservation.reservationNumber.toString().toLowerCase() : '';
          
          return (
            userName.includes(searchLower) ||
            customerName.includes(searchLower) ||
            customerEmail.includes(searchLower) ||
            customerPhone.includes(searchLower) ||
            reservationNumber.includes(searchLower)
          );
        });
        setFilteredReservations(filtered);
      } catch (error) {
        console.error("Error filtering reservations:", error);
        setFilteredReservations(allReservations);
      }
    }
  }, [searchTerm, allReservations]);

  // مزامنة تفاصيل اليوم المختار مع أحدث البيانات
  useEffect(() => {
    if (!dialogOpen || !selectedReservations.length) return;

    setSelectedReservations(prev =>
      prev.map(reservation => {
        const updated = allReservations.find(r => getReservationId(r) === getReservationId(reservation));
        return updated ? { ...reservation, ...updated } : reservation;
      })
    );
  }, [allReservations, dialogOpen]);

  // دالة للتعامل مع البحث
  const handleSearchChange = (event) => {
    try {
      const value = event?.target?.value || '';
      setSearchTerm(value);
    } catch (error) {
      console.error("Error in handleSearchChange:", error);
      setSearchTerm('');
    }
  };

  // دالة لمسح البحث
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const transformReservationsToEvents = useCallback(() => {
    const reservationsToUse = searchTerm.trim() ? filteredReservations : allReservations;
    
    if (!reservationsToUse || !Array.isArray(reservationsToUse) || reservationsToUse.length === 0) {
      console.log("No reservations data available or not an array:", reservationsToUse);
      return [];
    }
    
    console.log("🔄 Transform: Processing", reservationsToUse.length, "reservations");
    
    const validReservations = reservationsToUse.filter(reservation => {
      const isValid = validateReservationData(reservation);
      const isAccepted = reservation.status === "accepted";
      return isValid && isAccepted;
    });
    
    console.log("✅ Valid reservations:", validReservations.length);
    
    return validReservations.map((reservation) => {
      let startDate, endDate;
      try {
        if (reservation.dateOfReservation) {
          const originalStart = new Date(reservation.dateOfReservation);
          if (isNaN(originalStart.getTime())) {
            console.error("Invalid start date:", reservation.dateOfReservation);
            return null;
          }
          startDate = adjustDateForDisplay(originalStart);
          if (!startDate) return null;
        } else {
          console.error("Missing dateOfReservation:", reservation);
          return null;
        }
        
        if (reservation.dateEndOfReservation) {
          const originalEnd = new Date(reservation.dateEndOfReservation);
          if (isNaN(originalEnd.getTime())) {
            endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000);
          } else {
            endDate = adjustDateForDisplay(originalEnd);
            if (!endDate) {
              endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000);
            }
          }
        } else {
          endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000);
        }
      } catch (error) {
        console.error("Error processing reservation dates:", error, reservation);
        return null;
      }
      
      // تحديد لون الحدث بناءً على نوع الحجز
      let backgroundColor;
      let borderColor;
      let textColor = "#fff";
      
      // إذا كان حجز مميز، استخدم اللون الأصفر
      if (reservation.isSpecial || reservation.payWay === 'special') {
        backgroundColor = "#FFD700"; // أصفر ذهبي للحجوزات المميزة
        borderColor = "#FFC107";
        textColor = "#333"; // نص أسود للوضوح على الخلفية الصفراء
      } else {
        // للحجوزات العادية، استخدم الألوان حسب الحالة
        switch (reservation.status) {
          case "accepted":
            backgroundColor = primaryColor;
            borderColor = "#c4a769";
            break;
          case "pending":
            backgroundColor = "#f0c674";
            borderColor = "#e0b764";
            textColor = "#5f4d26";
            break;
          case "cancelled":
            backgroundColor = "#e57373";
            borderColor = "#d32f2f";
            break;
          default:
            backgroundColor = primaryColor;
            borderColor = "#c4a769";
        }
      }
      
      const displayName = reservation.customerName || reservation.userName || 'Unbekannt';
      const title = `${displayName} (${reservation.numberOfPersons || 1} P)`;
      
      return {
        id: reservation._id,
        title: title,
        start: startDate,
        end: endDate,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        textColor: textColor,
        extendedProps: {
          status: reservation.status,
          numberOfPersons: reservation.numberOfPersons || 1,
          packageName: reservation.packageName || "Standard",
          userName: displayName,
          reservationNumber: reservation.reservationNumber || (reservation._id ? reservation._id.slice(-3) : "000"),
          dateOfReservation: startDate.toISOString(),
          dateEndOfReservation: endDate.toISOString(),
          extraObjectsList: reservation.extraObjectsList || [],
          totalExtraObjectsPrice: reservation.totalExtraObjectsPrice,
          totalPrice: reservation.totalPrice,
          customerName: reservation.customerName || reservation.userName || "Unbekannter Benutzer",
          customerEmail: reservation.customerEmail || "Nicht angegeben",
          customerPhone: reservation.customerPhone || "Nicht angegeben",
          customerAddress: reservation.customerAddress || "",
          customerStreet: reservation.customerStreet || "",
          customerPLZ: reservation.customerPLZ || "",
          customerCity: reservation.customerCity || "",
          note: reservation.note || "",
          adminNote: reservation.adminNote || "",
          isAGift: reservation.isAGift || false,
          nameOfreceiver: reservation.nameOfreceiver || "",
          emailOfreceiver: reservation.emailOfreceiver || "",
          noteForReceiver: reservation.noteForReceiver || "",
          paymentMethod: reservation.payWay || "code",
          createdAt: reservation.createdAt || null,
          isSpecial: reservation.isSpecial || false,
          payWay: reservation.payWay || "code"
        }
      };
    }).filter(event => event !== null);
  }, [allReservations, filteredReservations, searchTerm, primaryColor]);

  const handleEventClick = (info) => {
    try {
      const eventStart = info.event.start;
      if (!eventStart || isNaN(eventStart.getTime())) {
        console.error("Invalid date in event:", info.event);
        setSelectedDate(new Date());
      } else {
        setSelectedDate(new Date(eventStart));
      }
      
      const res = info.event.extendedProps;
      
      if (res) {
        const selectedReservation = (allReservations.find(r => getReservationId(r) === getReservationId(res)) || res);
        setSelectedReservations([selectedReservation]);
      } else {
        setSelectedReservations([]);
      }
      
      setDialogOpen(true);
      info.jsEvent.preventDefault();
    } catch (error) {
      console.error("Error in handleEventClick:", error);
      setSelectedDate(new Date());
      setSelectedReservations([]);
      setDialogOpen(true);
    }
  };

  const handleDateClick = (arg) => {
    try {
      const clicked = new Date(arg.dateStr);
      
      if (isNaN(clicked.getTime())) {
        console.error("Invalid date clicked:", arg.dateStr);
        setSelectedDate(new Date());
      } else {
        setSelectedDate(clicked);
      }
      
      const reservationsForDate = filterReservationsByDate(allReservations, clicked);
      
      setSelectedReservations(reservationsForDate);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error in handleDateClick:", error);
      setSelectedDate(new Date());
      setSelectedReservations([]);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // دوال التعديل
  const getReservationId = (reservation) => {
    if (!reservation) return null;
    return (
      reservation._id ||
      reservation.id ||
      reservation.reservationId ||
      reservation.mongoId ||
      reservation.bookingId ||
      reservation.orderId ||
      null
    );
  };

  const normalizeReservation = (reservation) => {
    if (!reservation) return null;
    const resolvedId = getReservationId(reservation);
    if (!resolvedId) return reservation;
    return {
      ...reservation,
      _id: resolvedId,
      id: resolvedId,
    };
  };

  const fetchReservationWithBackendId = async (reservation) => {
    const normalized = normalizeReservation(reservation);
    if (getReservationId(normalized)) {
      return normalized;
    }

    if (!reservation?.reservationNumber) {
      return normalized;
    }

    try {
      console.log("🔍 EDIT: Resolving reservation ID for number:", reservation.reservationNumber);
      const response = await GetAllReservationsApi({
        page: 1,
        limit: 1,
        filters: { reservationNumber: reservation.reservationNumber }
      });

      const fetchedReservation = response?.data?.[0];
      if (fetchedReservation) {
        const mergedReservation = {
          ...reservation,
          ...fetchedReservation,
        };
        console.log("✅ EDIT: Resolved reservation ID:", fetchedReservation._id || fetchedReservation.id);
        return normalizeReservation(mergedReservation);
      }
      
      console.warn("⚠️ EDIT: No reservation found with number:", reservation.reservationNumber);
    } catch (error) {
      console.error("❌ EDIT: Failed to fetch reservation by number:", error);
    }

    return normalized;
  };

  const handleEdit = async (reservation) => {
    if (!reservation) return;

    setIsResolvingReservationId(true);
    const resolvedReservation = await fetchReservationWithBackendId(reservation);
    setIsResolvingReservationId(false);

    const reservationId = getReservationId(resolvedReservation);
    if (!reservationId) {
      console.error("❌ EDIT: Unable to resolve reservation ID for editing:", reservation);
      toastError("Reservierungs-ID nicht gefunden. Bitte Dialog schließen, neu laden und erneut versuchen.");
      return;
    }

    console.log("🔍 EDIT: Opening edit dialog for reservation:", resolvedReservation.reservationNumber || reservationId);
    console.log("🔍 EDIT: Original dateOfReservation:", resolvedReservation.dateOfReservation);
    console.log("🔍 EDIT: Original dateEndOfReservation:", resolvedReservation.dateEndOfReservation);
    
    setEditingReservation(resolvedReservation);
    
    // Convert UTC dates to local Date objects for DateTimePicker
    let startDate = null;
    let endDate = null;
    
    if (resolvedReservation.dateOfReservation) {
      const utcDate = new Date(resolvedReservation.dateOfReservation);
      startDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
    }
    
    if (resolvedReservation.dateEndOfReservation) {
      const utcDate = new Date(resolvedReservation.dateEndOfReservation);
      endDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );
    }
    
    console.log("🔍 EDIT: Converted startDate for picker:", startDate, startDate?.toISOString());
    console.log("🔍 EDIT: Converted endDate for picker:", endDate, endDate?.toISOString());
    
    setEditFormData({
      customerName: resolvedReservation.customerName || resolvedReservation.userName || "",
      customerEmail: resolvedReservation.customerEmail || "",
      customerPhone: resolvedReservation.customerPhone || "",
      customerStreet: resolvedReservation.customerStreet || "",
      customerPLZ: resolvedReservation.customerPLZ || "",
      customerCity: resolvedReservation.customerCity || "",
      status: resolvedReservation.status || "",
      adminNote: resolvedReservation.adminNote || "",
      note: resolvedReservation.note || "",
      dateOfReservation: startDate,
      dateEndOfReservation: endDate
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingReservation(null);
    setEditFormData({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerStreet: "",
      customerPLZ: "",
      customerCity: "",
      status: "",
      adminNote: "",
      note: "",
      dateOfReservation: null,
      dateEndOfReservation: null
    });
    setIsUpdatingTime(false);
  };

  const handleSaveEdit = async (payload) => {
    if (!editingReservation) return;
    setIsUpdatingTime(true);
    
    console.log("🔍 FRONTEND: Saving edit with payload:", payload);
    console.log("🔍 FRONTEND: Original reservation dates:", {
      dateOfReservation: editingReservation.dateOfReservation,
      dateEndOfReservation: editingReservation.dateEndOfReservation
    });
    
    // Convert dates to UTC ISO strings
    let dateOfReservationUTC = payload.dateOfReservation;
    let dateEndOfReservationUTC = payload.dateEndOfReservation;
    
    if (payload.dateOfReservation) {
      const localDate = new Date(payload.dateOfReservation);
      dateOfReservationUTC = new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes(),
        localDate.getSeconds()
      )).toISOString();
    }
    
    if (payload.dateEndOfReservation) {
      const localDate = new Date(payload.dateEndOfReservation);
      dateEndOfReservationUTC = new Date(Date.UTC(
        localDate.getFullYear(),
        localDate.getMonth(),
        localDate.getDate(),
        localDate.getHours(),
        localDate.getMinutes(),
        localDate.getSeconds()
      )).toISOString();
    }
    
    const processedPayload = {
      ...payload,
      dateOfReservation: dateOfReservationUTC,
      dateEndOfReservation: dateEndOfReservationUTC
    };
    
    console.log("🔍 FRONTEND: Processed payload with ISO dates:", processedPayload);
    
    try {
      const reservationId = getReservationId(editingReservation);
      if (!reservationId) {
        console.error("❌ FRONTEND: Missing reservation id for update in calendar", editingReservation);
        toastError("Reservierungs-ID nicht gefunden. Bitte Dialog schließen, neu öffnen und erneut versuchen.");
        setIsUpdatingTime(false);
        return;
      }
      const response = await FetchEditReservations(processedPayload, reservationId);
      
      if (response.status === 200) {
        toastSuccess("Reservierung erfolgreich aktualisiert!");
        
        const serverReservation = response?.data?.data || {};
        const updatesForList = {
          ...processedPayload,
          ...serverReservation,
          dateOfReservation: serverReservation.dateOfReservation || dateOfReservationUTC,
          dateEndOfReservation: serverReservation.dateEndOfReservation || dateEndOfReservationUTC
        };

        const personCount = response?.data?.data?.numberOfPersons ?? editingReservation?.numberOfPersons ?? processedPayload.numberOfPersons ?? 1;
        if (updatesForList.customerName) {
          updatesForList.userName = updatesForList.customerName;
        } else if (!updatesForList.userName) {
          updatesForList.userName = editingReservation?.userName || editingReservation?.customerName || "";
        }
        updatesForList.title = `${updatesForList.customerName || updatesForList.userName || 'Unbekannt'} (${personCount} P)`;
        
        setEditingReservation(prev => prev ? { ...prev, ...updatesForList } : prev);
        setEditFormData(prev => ({ ...prev, ...updatesForList }));
        
        // Close edit dialog and refresh main dialog
        setIsRefreshingSelection(true);
        setTimeout(() => {
          setIsUpdatingTime(false);
          handleCloseEditDialog();

          const applyLocalUpdate = (list = []) =>
            list.map(reservation => {
              const currentId = getReservationId(reservation);
              if (currentId !== reservationId) return reservation;
              
              return {
                ...reservation,
                ...updatesForList
              };
            });

          // Update selected reservations in the dialog
          setSelectedReservations(prev => applyLocalUpdate(prev));

          // Update calendar data without waiting for refetch
          setAllReservations(prev => applyLocalUpdate(prev));
          setFilteredReservations(prev => applyLocalUpdate(prev));

          // Trigger background refetch to keep data in sync
          dispatch(fetchdateOfReservationAutocomplete({
            page: 1,
            limit: ALL_RESERVATIONS_FETCH_LIMIT,
            dateOfReservation: "",
            isPaid: false
          })).unwrap().then((result) => {
            const refreshedData = Array.isArray(result?.data) ? result.data : [];
            setAllReservations(refreshedData);
            setFilteredReservations(prev => {
              if (!searchTerm.trim()) {
                return refreshedData;
              }
              return refreshedData.filter(reservation => {
                if (!reservation) return false;
                const searchLower = searchTerm.toLowerCase();
                const userName = reservation.userName ? reservation.userName.toString().toLowerCase() : '';
                const customerName = reservation.customerName ? reservation.customerName.toString().toLowerCase() : '';
                const customerEmail = reservation.customerEmail ? reservation.customerEmail.toString().toLowerCase() : '';
                const customerPhone = reservation.customerPhone ? reservation.customerPhone.toString().toLowerCase() : '';
                const reservationNumber = reservation.reservationNumber ? reservation.reservationNumber.toString().toLowerCase() : '';
                
                return (
                  userName.includes(searchLower) ||
                  customerName.includes(searchLower) ||
                  customerEmail.includes(searchLower) ||
                  customerPhone.includes(searchLower) ||
                  reservationNumber.includes(searchLower)
                );
              });
            });

            if (dialogOpen && selectedDate) {
              const refreshedSelection = filterReservationsByDate(refreshedData, selectedDate);
              setSelectedReservations(refreshedSelection);
            }
          }).catch(() => {}).finally(() => {
            setIsRefreshingSelection(false);
          });
        }, 1000);
      } else {
        const errorMsg = response?.data?.message || response?.error || `Fehler beim Aktualisieren: Status ${response.status}`;
        toastError(errorMsg);
        setIsUpdatingTime(false);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || "Fehler beim Aktualisieren der Reservierung. Bitte erneut versuchen.";
      toastError(serverMessage);
      setIsUpdatingTime(false);
    }
  };

  if (data.loading && !allReservations.length) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (data.error && !allReservations.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Fehler beim Laden der Reservierungen: {data.error}
        </Typography>
      </Box>
    );
  }

  const formatTimeForCalendar = (dateString) => {
    try {
      if (!dateString) return "00:00";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "00:00";
      return format(date, "HH:mm");
    } catch (error) {
      console.error("Error formatting time for calendar:", error);
      return "00:00";
    }
  };

  const formatTimeForDetails = (dateString, isSpecial = false) => {
    return formatTimeForDisplay(dateString);
  };

  const renderReservationDialog = () => (
    <Dialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle 
        sx={{
          backgroundColor: "#e6c88a",
          color: "#fff",
          fontWeight: "bold",
          py: 2.5,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="span" sx={{ 
            backgroundColor: "rgba(255,255,255,0.2)", 
            borderRadius: "50%",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1
          }}>
            {selectedDate && !isNaN(selectedDate.getTime()) ? format(selectedDate, "dd") : "--"}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: "normal", mb: 0.5 }}>
              {selectedDate && !isNaN(selectedDate.getTime()) ? format(selectedDate, "EEEE", { locale: de }) : "---"}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {selectedDate && !isNaN(selectedDate.getTime()) ? format(selectedDate, "MMMM yyyy", { locale: de }) : "---"}
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`${selectedReservations.length} Reservierung${selectedReservations.length !== 1 ? 'en' : ''}`} 
          sx={{ 
            backgroundColor: "rgba(255,255,255,0.2)", 
            color: "#fff",
            fontWeight: "bold"
          }} 
        />
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {(isUpdatingTime || isRefreshingSelection) && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5
          }}>
            <CircularProgress size={42} sx={{ color: '#D6B981' }} />
            <Typography variant="h6" sx={{ color: '#7a6948', fontWeight: 600 }}>
              Aktualisiere Reservierung...
            </Typography>
            <Typography variant="body2" sx={{ color: '#b09a6b', maxWidth: 280, textAlign: 'center' }}>
              Bitte warten Sie einen Moment, bis die neuesten Kundendaten geladen wurden.
            </Typography>
          </Box>
        )}
        {selectedReservations.length === 0 ? (
          <Box sx={{ 
            py: 8, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center",
            color: "text.secondary"
          }}>
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: "50%", 
              backgroundColor: "#f5f5f5", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              mb: 2
            }}>
              <Typography variant="h4"> </Typography>
            </Box>
            <Typography variant="h6" color="text.secondary">
              Keine Reservierungen gefunden
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Für diesen Tag sind keine Buchungen vorhanden
            </Typography>
          </Box>
        ) : (
          <Box>
            {selectedReservations.map((res, index) => (
              <Box 
                key={res._id || index} 
                sx={{ 
                  p: 3,
                  borderBottom: index < selectedReservations.length - 1 ? "1px solid #eee" : "none",
                  transition: "background-color 0.2s",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "#f9f9f9"
                  }
                }}
              >
                {isUpdatingTime && editingReservation && getReservationId(editingReservation) === getReservationId(res) && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    zIndex: 5,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}>
                    <CircularProgress size={32} sx={{ color: '#D6B981' }} />
                    <Typography variant="body2" sx={{ color: '#9c8b68', fontWeight: 600 }}>
                      Aktualisiere Daten...
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: "50%", 
                          backgroundColor: getPaymentDotColor(res),
                          mr: 1.5,
                          border: '1px solid rgba(255,255,255,0.3)'
                        }} 
                      />
                      <Typography variant="h6" sx={{ 
                        fontWeight: "bold",
                        color: "#333"
                      }}>
                        {res.customerName || res.userName || "Unbekannter Benutzer"}
                      </Typography>
                      {(res.isSpecial || res.paymentMethod === 'special') && (
                        <StarIcon sx={{ ml: 1, color: "#FFD700", fontSize: "1.2rem" }} />
                      )}
                      <IconButton
                        onClick={() => handleEdit(res)}
                        disabled={isResolvingReservationId || isUpdatingTime}
                        size="small"
                        sx={{
                          ml: 1,
                          color: "#D6B981",
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            color: '#C5A572'
                          }
                        }}
                        title="Reservierung bearbeiten"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                      Reservierung #{res.reservationNumber || (res._id ? res._id.slice(-3) : index)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {res.status === "accepted" && (
                      <Chip
                        label="Bestätigt"
                        color="success"
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    )}
                    {(res.isSpecial || res.paymentMethod === 'special') && (
                      <Chip
                        label="Spezial"
                        sx={{ 
                          backgroundColor: "#FFD700",
                          color: "#333",
                          fontWeight: "bold"
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ 
                  display: "grid", 
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, 
                  gap: 3, 
                  mb: 2,
                  ml: 3.5
                }}>
                  {/* معلومات العميل الشخصية */}
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: 1.5,
                    backgroundColor: "#f0f8ff",
                    borderRadius: "12px",
                    p: 2,
                    border: "1px solid #e3f2fd"
                  }}>
                    <Typography variant="subtitle2" sx={{ color: "#1976d2", mb: 0.5, fontWeight: 600 }}>
                      Kundeninformationen
                    </Typography>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Name:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {res.customerName || res.userName || "Nicht angegeben"}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>E-Mail:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
                        {res.customerEmail || "Nicht angegeben"}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Telefon:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {res.customerPhone || "Nicht angegeben"}
                      </Typography>
                    </Box>
                    
                    {res.customerStreet && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>Straße:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
                          {res.customerStreet}
                        </Typography>
                      </Box>
                    )}
                    
                    {(res.customerPLZ || res.customerCity) && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>PLZ / Stadt:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
                          {res.customerPLZ} {res.customerCity}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* تفاصيل الحجز */}
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: 1.5,
                    backgroundColor: "#f9f9f9",
                    borderRadius: "12px",
                    p: 2
                  }}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Reservierungsdetails
                    </Typography>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Zeit:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatTimeForDetails(res.dateOfReservation, res.isSpecial || res.payWay === 'special')} - {formatTimeForDetails(res.dateEndOfReservation || (res.dateOfReservation ? new Date(new Date(res.dateOfReservation).getTime() + 4 * 60 * 60 * 1000) : null), res.isSpecial || res.payWay === 'special')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Personen:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{res.numberOfPersons || 1}</Typography>
                    </Box>
                    
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Paket:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{res.packageName || "Hamam Luxus"}</Typography>
                    </Box>
                    
                    {/* إضافة طريقة الدفع مع النقطة الملونة */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>Zahlungsart:</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: "50%", 
                            backgroundColor: getPaymentDotColor(res),
                            border: '1px solid rgba(0,0,0,0.1)'
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {(res.isSpecial || res.paymentMethod === 'special') ? 'Spezial' : 
                           (res.paymentMethod === 'paypal' ? 'PayPal' : 'Code')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* الملاحظات: ملاحظة العميل وملاحظة الأدمن */}
                <Box sx={{ 
                  backgroundColor: "#fffdf5",
                  borderRadius: "12px",
                  p: 2,
                  mt: 2,
                  border: "1px solid #f0e2c1",
                  ml: 3.5
                }}>
                  <Typography variant="subtitle2" sx={{ color: "#8d6e00", mb: 1, fontWeight: 600 }}>
                    Notizen
                  </Typography>
                  {res.note || res.adminNote ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {res.adminNote && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>Admin-Notiz:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", whiteSpace: "pre-wrap" }}>
                            {res.adminNote}
                          </Typography>
                        </Box>
                      )}
                      {res.note && (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>Kunden-Notiz:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", whiteSpace: "pre-wrap" }}>
                            {res.note}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Keine Notizen
                    </Typography>
                  )}
                </Box>

                {/* قسم الإكسترا - أسفل بوكس Notizen مباشرة */}
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: 1.5,
                  backgroundColor: "#fff8e1",
                  borderRadius: "12px",
                  p: 2,
                  mt: 2,
                  border: "1px solid #ffcc02",
                  ml: 3.5
                }}>
                  <Typography variant="subtitle2" sx={{ color: "#e65100", mb: 0.5, fontWeight: 600 }}>
                      Extras & Zusatzleistungen
                  </Typography>
                  
                  {res.extraObjectsList && res.extraObjectsList.length > 0 ? (
                    <>
                      {res.extraObjectsList.map((item, idx) => (
                        <Box key={idx} sx={{ 
                          display: "flex", 
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: "rgba(255, 255, 255, 0.7)",
                          borderRadius: "8px",
                          p: 1.5,
                          border: "1px solid rgba(255, 204, 2, 0.3)"
                        }}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              Anzahl: {item.number} × {item.price}€
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 700, 
                            color: "#e65100",
                            backgroundColor: "rgba(230, 81, 0, 0.1)",
                            padding: "4px 8px",
                            borderRadius: "6px"
                          }}>
                            {(item.price * item.number).toFixed(2)}€
                          </Typography>
                        </Box>
                      ))}
                      
                      {res.totalExtraObjectsPrice !== undefined && (
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          mt: 1, 
                          pt: 2, 
                          borderTop: "2px dashed #ffcc02",
                          backgroundColor: "rgba(255, 204, 2, 0.1)",
                          borderRadius: "8px",
                          p: 1.5
                        }}>
                          <Typography variant="subtitle1" sx={{ color: "#e65100", fontWeight: 700 }}>
                            Extras Gesamtpreis:
                          </Typography>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 800, 
                            color: "#e65100",
                            fontSize: "1.1rem"
                          }}>
                            {res.totalExtraObjectsPrice.toFixed(2)}€
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "8px",
                      p: 2,
                      border: "1px dashed rgba(255, 204, 2, 0.5)"
                    }}>
                      <Typography variant="body2" sx={{ 
                        color: "text.secondary", 
                        fontStyle: "italic",
                        textAlign: "center"
                      }}>
                        ❌ Keine Extras für diese Reservierung
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* السعر الكلي للحجز */}
                <Box sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  backgroundColor: "#f5f5f5",
                  borderRadius: "12px",
                  p: 2,
                  mt: 2,
                  borderLeft: "4px solid #CD9D41"
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
                    Gesamtpreis:
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#CD9D41" }}>
                    {res.totalPrice}€
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          sx={{
            borderColor: '#D6B981',
            color: '#6b5b3e',
            '&:hover': { 
              borderColor: '#C5A572', 
              backgroundColor: '#faf7ef' 
            }
          }}
        >
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Edit Dialog Component
  const EditDialog = () => {
    const [localFormData, setLocalFormData] = React.useState(editFormData);
    React.useEffect(() => {
      if (editDialogOpen) {
        setLocalFormData(editFormData);
      }
    }, [editDialogOpen, editFormData]);

    const updateField = (field, value) => {
      setLocalFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleLocalSave = () => {
      console.log("🔍 FRONTEND EditDialog: Saving with localFormData:", localFormData);
      console.log("🔍 FRONTEND EditDialog: dateOfReservation type:", typeof localFormData.dateOfReservation, localFormData.dateOfReservation);
      console.log("🔍 FRONTEND EditDialog: dateEndOfReservation type:", typeof localFormData.dateEndOfReservation, localFormData.dateEndOfReservation);
      handleSaveEdit(localFormData);
    };

    return (
      <Dialog 
        open={editDialogOpen} 
        onClose={isUpdatingTime ? undefined : handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            position: 'relative'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
            color: '#fff',
            fontWeight: 'bold'
          }}
        >
          Reservierung bearbeiten Nr.: {editingReservation?.reservationNumber || editingReservation?._id?.slice(-6)}
        </DialogTitle>
        <DialogContent sx={{ pt: '30px !important', px: 3, pb: 3, backgroundColor: '#faf7ef', position: 'relative' }}>
          {/* Loading Overlay داخل Dialog */}
          {isUpdatingTime && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0 0 12px 12px'
            }}>
              <CircularProgress size={50} sx={{ color: '#D6B981', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold', mb: 1 }}>
                Termin wird aktualisiert...
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', maxWidth: 300 }}>
                Bitte warten Sie, während wir den Termin aktualisieren und eine E-Mail senden.
              </Typography>
            </Box>
          )}
          
          <Grid container spacing={2} sx={{ mt: 0, opacity: isUpdatingTime ? 0.5 : 1, pointerEvents: isUpdatingTime ? 'none' : 'auto' }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kundenname"
                value={localFormData.customerName}
                onChange={(e) => updateField('customerName', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={localFormData.customerEmail}
                onChange={(e) => updateField('customerEmail', e.target.value)}
                variant="outlined"
                size="small"
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f5f5',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefonnummer"
                value={localFormData.customerPhone}
                onChange={(e) => updateField('customerPhone', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Straße und Hausnummer"
                value={localFormData.customerStreet}
                onChange={(e) => updateField('customerStreet', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postleitzahl"
                value={localFormData.customerPLZ}
                onChange={(e) => updateField('customerPLZ', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stadt"
                value={localFormData.customerCity}
                onChange={(e) => updateField('customerCity', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFormData.status}
                  label="Status"
                  onChange={(e) => updateField('status', e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E6D7B8' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D6B981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C5A572' }
                  }}
                >
                  <MenuItem value="pending">Ausstehend</MenuItem>
                  <MenuItem value="accepted">Angenommen</MenuItem>
                  <MenuItem value="rejected">Abgelehnt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin-Notiz"
                multiline
                rows={3}
                value={localFormData.adminNote}
                onChange={(e) => updateField('adminNote', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kunden-Notiz"
                multiline
                rows={3}
                value={localFormData.note}
                onChange={(e) => updateField('note', e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover fieldset': { borderColor: '#D6B981' },
                    '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                  }
                }}
              />
            </Grid>
            
            {/* حقول التاريخ والوقت */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #fff8e1 0%, #ffe9a8 100%)',
                border: '2px solid #D6B981'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#D6B981', mb: 2 }}>
                  Datum und Uhrzeit ändern
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Startdatum und -zeit"
                        value={localFormData.dateOfReservation}
                        onChange={(newValue) => updateField('dateOfReservation', newValue)}
                        format="dd.MM.yyyy HH:mm"
                        ampm={false}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                '&:hover fieldset': { borderColor: '#D6B981' },
                                '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Enddatum und -zeit"
                        value={localFormData.dateEndOfReservation}
                        onChange={(newValue) => updateField('dateEndOfReservation', newValue)}
                        format="dd.MM.yyyy HH:mm"
                        ampm={false}
                        minDateTime={localFormData.dateOfReservation}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                '&:hover fieldset': { borderColor: '#D6B981' },
                                '&.Mui-focused fieldset': { borderColor: '#C5A572' }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                  <Typography variant="caption" sx={{ color: '#856404', display: 'block', fontWeight: 'bold' }}>
                    ⚠️ Hinweis: Wenn Sie das Datum oder die Uhrzeit ändern, wird automatisch eine E-Mail mit den neuen Zeiten an den Kunden gesendet.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, backgroundColor: '#fff', position: 'relative' }}>
          {isUpdatingTime && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
              borderRadius: '0 0 12px 12px'
            }} />
          )}
          <Button 
            onClick={handleCloseEditDialog} 
            variant="outlined"
            disabled={isUpdatingTime}
            sx={{
              borderColor: '#D6B981',
              color: '#6b5b3e',
              '&:hover': { borderColor: '#C5A572', backgroundColor: '#faf7ef' },
              position: 'relative',
              zIndex: 1
            }}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleLocalSave} 
            variant="contained"
            disabled={isUpdatingTime}
            sx={{
              background: 'linear-gradient(135deg, #D6B981 0%, #C5A572 100%)',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': { background: 'linear-gradient(135deg, #C5A572 0%, #B8A572 100%)' },
              position: 'relative',
              zIndex: 1,
              minWidth: 180
            }}
            startIcon={isUpdatingTime ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
          >
            {isUpdatingTime ? 'Wird gespeichert...' : 'Änderungen speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <StyleWrapper sx={{ height: "100%" }}>
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        mb: 3
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#333" }}>
          Reservierungskalender
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* زر إضافة حجز مميز - نفس تصميم الصفحة الرئيسية */}
          <Button
            onClick={handleAddSpecialReservation}
            sx={{
              mb: { xs: "16px", md: "0px" },
              flexShrink: 1,
              backgroundColor: '#FFD700',
              borderRadius: '8px',
              color: 'black',
              fontSize: '14px',
              height: '46px',
              px: '10px',
              pl: 0,
              '&:hover': {
                backgroundColor: '#FFC107'
              }
            }}
            startIcon={
              <Box
                sx={{
                  backgroundColor: '#FFC107',
                  height: "100%",
                  py: 1.3,
                  px: 1,
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <StarIcon sx={{ color: 'white' }} />
              </Box>
            }
          >
            <Typography sx={{
              fontSize: { md: "16px", xs: "12px" },
              textTransform: "none"
            }}>
              Sonderreservierung
            </Typography>
          </Button>
          <CustomButton
            onNavigate={() => navigate("/AddBuchungen")}
            title="Neue Buchung"
          />
        </Box>
      </Box>
      
      {/* شريط البحث */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Nach Name, E-Mail, Telefon oder Reservierungsnummer suchen..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: primaryColor }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{ color: "text.secondary" }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: "12px",
              backgroundColor: "#fff",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${primaryColor}30`,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: `${primaryColor}60`,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
              },
            }
          }}
        />
        {searchTerm && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              {Array.isArray(filteredReservations) ? filteredReservations.length : 0} von {Array.isArray(allReservations) ? allReservations.length : 0} Reservierungen gefunden
            </Typography>
          </Box>
        )}
      </Box>
      
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          height: "80vh", 
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
          "& .fc-event": {
            borderRadius: "6px",
            border: "none",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            overflow: "hidden"
          },
          "& .fc-daygrid-day-number": {
            fontWeight: "500",
            padding: "8px"
          },
          "& .fc-daygrid-day-top": {
            justifyContent: "center"
          },
          "& .fc-day-today": {
            backgroundColor: `${primaryColor}15`
          },
          "& .fc-button-primary": {
            backgroundColor: primaryColor,
            borderColor: primaryColor,
            "&:hover": {
              backgroundColor: "#c4a769",
              borderColor: "#c4a769"
            },
            "&:disabled": {
              backgroundColor: `${primaryColor}80`,
              borderColor: `${primaryColor}80`
            }
          },
          "& .fc-button-primary:not(:disabled).fc-button-active": {
            backgroundColor: "#a57122",
            borderColor: "#a57122"
          },
          "& .fc-daygrid-more-link": {
            backgroundColor: "#f5f5f5",
            color: "#333",
            fontWeight: "bold",
            padding: "2px 6px",
            borderRadius: "4px",
            margin: "2px 0",
            textAlign: "center"
          },
          "& .fc-h-event .fc-event-title": {
            padding: "4px 6px"
          }
        }}
      >
      <FullCalendar
          timeZone="UTC"
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          buttonText={{
            today: "Heute",
            month: "Monat",
            week: "Woche",
            day: "Tag",
            list: "Liste",
          }}
          locale={deLocale}
          events={transformReservationsToEvents()}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDisplay="block"
          eventTimeFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          allDayText="gan. Tag"
          height="100%"
          nowIndicator={false}
          selectable
          editable
          dayMaxEvents={2}
          moreLinkClick={(info) => {
            const clickedDate = new Date(info.date);
            setSelectedDate(clickedDate);
            
            const reservationsForDate = filterReservationsByDate(allReservations, clickedDate);
            
            setSelectedReservations(reservationsForDate);
            setDialogOpen(true);
            return false;
          }}
          eventContent={(info) => (
            <Box sx={{ 
              p: 0.5, 
              overflow: "hidden",
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              borderRadius: "4px",
              backgroundColor: info.event.backgroundColor,
              border: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}>
              {/* نقطة طريقة الدفع - أصفر للمميز، أخضر للبايبال، أحمر للكود */}
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: "50%", 
                backgroundColor: getPaymentDotColor(info.event.extendedProps),
                flexShrink: 0,
                border: '1px solid rgba(255,255,255,0.3)'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "0.8rem",
                  color: info.event.textColor
                }}
              >
                {info.event.title}
              </Typography>
              {(info.event.extendedProps.isSpecial || info.event.extendedProps.paymentMethod === 'special') && (
                <StarIcon sx={{ fontSize: "0.8rem", color: info.event.textColor, flexShrink: 0 }} />
              )}
            </Box>
          )}
          titleFormat={{ year: "numeric", month: "long", day: "numeric" }}
        />
      </Paper>
      
      {renderReservationDialog()}
      
      <EditDialog />
      
      {/* Special Reservation Dialog */}
      <SpecialReservationDialog
        open={specialReservationDialogOpen}
        onClose={handleCloseSpecialReservation}
        onSubmit={handleCreateSpecialReservation}
        loading={specialReservationLoading}
      />
    </StyleWrapper>
  );
};

export default KalenderReservierung;